/**
 * Compact row of status badges. Each badge renders only when active so
 * the row collapses to nothing in the (very common) all-clear case.
 *
 * Badge vocabulary mirrors `MechState` flags + counts that the engine
 * surfaces (see server/mecharena/state.py). Colors come from semantic
 * tokens — danger for shutdown/knocked, warn for stagger/heat-adj
 * effects, muted for passive flags (tagged/scanned).
 */

import type { CSSProperties } from "react";
import type { StatusFlags, CritCounts } from "./derive";
import { hasAnyCrits } from "./derive";

export interface StatusBadgesProps {
  flags: StatusFlags;
  crits: CritCounts;
}

const rowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 4,
  width: "100%",
};

const badgeBase: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  padding: "2px 6px",
  borderRadius: "var(--pp-radius)",
  border: "1px solid var(--pp-border)",
  fontSize: "var(--pp-font-sm)",
  fontWeight: "var(--pp-weight-bold)",
  letterSpacing: "var(--pp-tracking-caps)",
  textTransform: "uppercase",
  fontFamily: "var(--pp-font-family)",
  background: "var(--pp-bg)",
  color: "var(--pp-text)",
};

const dangerBadge: CSSProperties = {
  ...badgeBase,
  borderColor: "var(--pp-danger)",
  color: "var(--pp-danger)",
};

const warnBadge: CSSProperties = {
  ...badgeBase,
  borderColor: "var(--pp-warn)",
  color: "var(--pp-warn)",
};

const mutedBadge: CSSProperties = {
  ...badgeBase,
  color: "var(--pp-text-muted)",
};

const countStyle: CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  marginLeft: 2,
};

interface BadgeProps {
  label: string;
  style?: CSSProperties;
  count?: number;
}

function Badge({ label, style, count }: BadgeProps) {
  return (
    <span style={style ?? badgeBase}>
      {label}
      {count !== undefined && count > 1 ? <span style={countStyle}>×{count}</span> : null}
    </span>
  );
}

export function StatusBadges({ flags, crits }: StatusBadgesProps) {
  const empty =
    !flags.shutdown &&
    !flags.staggered &&
    !flags.knockedDown &&
    !flags.smoke &&
    !flags.tagged &&
    !flags.ecm &&
    flags.targetLocks === 0 &&
    !flags.scanned &&
    !hasAnyCrits(crits);
  if (empty) return null;

  return (
    <div style={rowStyle} aria-label="Status">
      {flags.shutdown ? <Badge label="SHUTDOWN" style={dangerBadge} /> : null}
      {flags.knockedDown ? <Badge label="DOWN" style={dangerBadge} /> : null}
      {flags.staggered ? <Badge label="STAGGER" style={warnBadge} /> : null}
      {flags.targetLocks > 0 ? (
        <Badge label="LOCK" count={flags.targetLocks} style={warnBadge} />
      ) : null}
      {flags.smoke ? <Badge label="SMOKE" style={mutedBadge} /> : null}
      {flags.ecm ? <Badge label="ECM" style={mutedBadge} /> : null}
      {flags.tagged ? <Badge label="TAGGED" style={mutedBadge} /> : null}
      {flags.scanned ? <Badge label="SCAN" style={mutedBadge} /> : null}
      {crits.engine > 0 ? (
        <Badge label="ENGINE" count={crits.engine} style={dangerBadge} />
      ) : null}
      {crits.gyro > 0 ? <Badge label="GYRO" count={crits.gyro} style={dangerBadge} /> : null}
      {crits.lLegActuator > 0 ? (
        <Badge label="L-ACT" count={crits.lLegActuator} style={warnBadge} />
      ) : null}
      {crits.rLegActuator > 0 ? (
        <Badge label="R-ACT" count={crits.rLegActuator} style={warnBadge} />
      ) : null}
      {crits.destroyedTools > 0 ? (
        <Badge label="TOOL" count={crits.destroyedTools} style={dangerBadge} />
      ) : null}
    </div>
  );
}
